import React from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { FaGem, FaHammer, FaMountain, FaTools, FaGraduationCap, FaBookOpen, FaUsers, FaLightbulb, FaCheckCircle, FaClock, FaMapMarkerAlt, FaCertificate } from "react-icons/fa";
import Helmet from "../components/Helmet/Helmet";
import heroImage from "../assets/all-images/bg16.jpg";
import workshopImage from "../assets/all-images/bg16.jpg";
import gemstoneImage from "../assets/all-images/bg11.jpg";
import bannerImage from "../assets/all-images/banner.jpg";
import founderImage from "../assets/all-images/ceo2.jpg";
import course1Image from "../assets/course1.jpeg";
import course2Image from "../assets/course2.jpeg";
import course3Image from "../assets/course3.jpeg";
import course4Image from "../assets/course4.jpeg";
import { siteConfig } from "../utils/site";
import "../styles/luxury-pages.css";
import "../styles/world-class-luxury.css";

const phases = [
  {
    phase: "Phase 01",
    title: "Gemstone Foundations",
    icon: <FaGem />,
    image: course1Image,
    topics: [
      "Introduction to Ethiopian gemstones and the global jewellery value chain",
      "Basic gemstone identification, grading language, and care principles",
      "Ethics, safety, and responsible sourcing awareness",
      "Understanding gemstone markets and valuation basics"
    ],
  },
  {
    phase: "Phase 02",
    title: "Mining & Field Knowledge",
    icon: <FaMountain />,
    image: course2Image,
    topics: [
      "How gemstone mining works from exploration to extraction",
      "Basic field tools, site workflow, sorting, and first-stage handling",
      "Mining safety, team roles, and early-stage quality selection",
      "Environmental considerations and sustainable practices"
    ],
  },
  {
    phase: "Phase 03",
    title: "Silver & Gold Workshop Basics",
    icon: <FaHammer />,
    image: course3Image,
    topics: [
      "Core bench tools, workshop setup, and safe handling of materials",
      "Basic silver and gold forming, soldering, finishing, and polishing",
      "Simple ring, pendant, and setting preparation techniques",
      "Quality control and workshop safety protocols"
    ],
  },
  {
    phase: "Phase 04",
    title: "Retail & Shop Practice",
    icon: <FaTools />,
    image: course4Image,
    topics: [
      "How to present gemstones and jewellery professionally in a shop",
      "Customer education, trust-building, and product storytelling",
      "Workshop-to-showroom workflow for quality control and final presentation",
      "Sales techniques and client relationship management"
    ],
  },
];

const outcomes = [
  "Understand how gemstones move from mine to market",
  "Recognize the basics of silver and gold jewellery bench work",
  "Build stronger confidence for retail, workshop, or sourcing roles",
  "Prepare for more advanced Roha training phases in the future",
  "Develop professional communication about gemstones and jewellery",
  "Gain practical skills applicable in real-world jewellery environments"
];

const RohaCourse = () => {
  return (
    <>
      <Helmet
        title="Roha Course | Gemstone Mining and Jewellery Craft Training"
        description="Explore the upcoming Roha Course curriculum covering gemstone mining, gemstone basics, and foundational silver and gold workshop skills in a phase-based learning journey."
        keywords="gemstone course Ethiopia, gemstone mining course, jewellery training Ethiopia, silver work course, gold work course, Roha course, gemstone education Addis Ababa"
        canonical={`${siteConfig.url}/course`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          name: "Roha Course",
          description:
            "Upcoming phase-based Roha educational program covering gemstone mining, gemstone knowledge, and basic silver and gold workshop skills.",
          provider: {
            "@type": "Organization",
            name: siteConfig.name,
            url: siteConfig.url,
          },
        }}
      />

      <main className="luxury-page">
        {/* Hero Section */}
        <section className="luxury-page__hero">
          <div className="luxury-page__hero-bg">
            <img src={heroImage} alt="Roha Course Gemstone Training Background" />
          </div>
          <div className="luxury-page__hero-overlay"></div>
          <div className="luxury-page__hero-gradient"></div>
          
          <Container>
            <div className="luxury-page__hero-content">
              <div className="luxury-page__hero-eyebrow">
                <FaGraduationCap /> Roha Course
              </div>
              
              <h1 className="luxury-page__hero-title">
                Learn <span className="luxury-page__hero-title-gradient">Gemstone Mining</span> and Jewellery Craft
              </h1>
              
              <p className="luxury-page__hero-subtitle">
                Roha Course is being prepared as a phase-based learning experience for future gemstone workers, 
                jewellery makers, and shop teams who want stronger foundations in Ethiopian gemstone knowledge 
                and workshop practice.
              </p>
              
              <div className="luxury-page__hero-actions">
                <Link to="/contact" className="wc-button wc-button-primary wc-button-lg">
                  Ask About Enrollment
                </Link>
                <Link to="/about" className="wc-button wc-button-secondary">
                  Learn About Roha
                </Link>
              </div>
              
              <div className="luxury-page__hero-tags">
                <span className="luxury-page__hero-tag"><FaClock /> Coming Soon</span>
                <span className="luxury-page__hero-tag"><FaBookOpen /> Phase-Based Curriculum</span>
                <span className="luxury-page__hero-tag"><FaMountain /> Mining to Workshop</span>
                <span className="luxury-page__hero-tag"><FaCertificate /> Retail-Ready Knowledge</span>
              </div>
            </div>
          </Container>
        </section>

        {/* Course Overview Section */}
        <section className="luxury-section luxury-section--soft">
          <Container>
            <div className="luxury-section__heading">
              <div className="luxury-section__eyebrow">
                <FaLightbulb /> Course Vision
              </div>
              <h2 className="luxury-section__title">Built for Learners Who Want to Understand Both the Stone and the Craft</h2>
              <p className="luxury-section__subtitle">
                The Roha Course is planned as a practical, professional introduction to gemstone mining, 
                gemstone knowledge, and foundational silver and gold work for workshop and shop environments.
              </p>
            </div>
            
            <div className="luxury-grid luxury-grid--3">
              {outcomes.map((item, index) => (
                <div key={index} className="luxury-card">
                  <div className="luxury-card__body">
                    <div className="luxury-card__badge" style={{ position: 'relative', top: 0, left: 0, marginBottom: '1rem' }}>
                      <FaCheckCircle /> Outcome {index + 1}
                    </div>
                    <h3 className="luxury-card__title">
                      <a href="#">{item}</a>
                    </h3>
                    <p className="luxury-card__description">
                      This learning path is designed to create stronger confidence before moving into deeper technical training.
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Curriculum Phases Section */}
        <section className="luxury-section">
          <Container>
            <div className="luxury-section__heading">
              <div className="luxury-section__eyebrow">
                <FaBookOpen /> Phase-Based Curriculum
              </div>
              <h2 className="luxury-section__title">Structured in Clear Training Phases</h2>
              <p className="luxury-section__subtitle">
                Each phase builds upon the previous, creating a comprehensive learning journey from gemstone origin to finished jewellery.
              </p>
            </div>
            
            <div className="luxury-grid luxury-grid--2">
              {phases.map((phase) => (
                <div key={phase.phase} className="luxury-course-phase">
                  <div className="luxury-course-phase__number">
                    {phase.phase}
                  </div>
                  
                  <div className="luxury-course-phase__icon">
                    {phase.icon}
                  </div>
                  
                  <img
                    src={phase.image}
                    alt={phase.title}
                    className="luxury-course-phase__image"
                    loading="lazy"
                  />
                  
                  <h3 className="luxury-course-phase__title">{phase.title}</h3>
                  
                  <div className="luxury-course-phase__topics">
                    {phase.topics.map((topic, index) => (
                      <div key={index} className="luxury-course-phase__topic">
                        <div className="luxury-course-phase__topic-icon">
                          <FaCheckCircle />
                        </div>
                        <p className="luxury-course-phase__topic-text">{topic}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="luxury-course-phase__actions">
                    <button type="button" className="wc-button wc-button-primary" disabled>
                      Coming Soon
                    </button>
                    <Link to="/contact" className="wc-button wc-button-secondary">
                      Ask About This Phase
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Target Audience Section */}
        <section className="luxury-section luxury-section--soft">
          <Container>
            <div className="luxury-section__heading">
              <div className="luxury-section__eyebrow">
                <FaUsers /> Who It Is For
              </div>
              <h2 className="luxury-section__title">Designed for Future Makers, Sellers, and Gemstone Learners</h2>
              <p className="luxury-section__subtitle">
                Whether you're starting your journey or looking to deepen your existing knowledge, 
                the Roha Course provides practical foundations for success in the jewellery industry.
              </p>
            </div>
            
            <div className="luxury-grid luxury-grid--3">
              <div className="luxury-card">
                <div className="luxury-card__body">
                  <h3 className="luxury-card__title">
                    <a href="#">Beginners Entering the Field</a>
                  </h3>
                  <p className="luxury-card__description">
                    Start with practical language, safe habits, and clear fundamentals. Learn the essential 
                    terminology and processes that form the foundation of the jewellery industry.
                  </p>
                  <div className="luxury-card__link">
                    Perfect for career starters <span>→</span>
                  </div>
                </div>
              </div>
              
              <div className="luxury-card">
                <div className="luxury-card__body">
                  <h3 className="luxury-card__title">
                    <a href="#">Workshop Assistants & Junior Makers</a>
                  </h3>
                  <p className="luxury-card__description">
                    Build confidence around basic silver and gold bench processes. Enhance your technical 
                    skills and understanding of materials, tools, and workshop protocols.
                  </p>
                  <div className="luxury-card__link">
                    Skill development focus <span>→</span>
                  </div>
                </div>
              </div>
              
              <div className="luxury-card">
                <div className="luxury-card__body">
                  <h3 className="luxury-card__title">
                    <a href="#">Retail & Jewellery Shop Teams</a>
                  </h3>
                  <p className="luxury-card__description">
                    Learn how to speak about gemstones and finished pieces more professionally. Develop 
                    product knowledge, customer service skills, and sales techniques specific to luxury jewellery.
                  </p>
                  <div className="luxury-card__link">
                    Professional communication <span>→</span>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* Learning Environment Section */}
        <section className="luxury-section">
          <Container>
            <div className="luxury-section__heading">
              <div className="luxury-section__eyebrow">
                <FaMapMarkerAlt /> Learning Atmosphere
              </div>
              <h2 className="luxury-section__title">Practical, Grounded, and Close to the Realities of the Trade</h2>
              <p className="luxury-section__subtitle">
                Roha Course is intended to feel useful from the beginning, combining gemstone field understanding, 
                workshop basics, and shop-facing confidence for people entering the jewellery world.
              </p>
            </div>
            
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div className="luxury-card">
                  <div className="luxury-card__image-wrapper">
                    <img
                      src={founderImage}
                      alt="Roha Course leadership and mentorship"
                      className="luxury-card__image"
                      loading="lazy"
                    />
                    <div className="luxury-card__overlay"></div>
                    <div className="luxury-card__badge">
                      <FaUsers /> Expert-Led Training
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="col-lg-6">
                <div className="ps-lg-5">
                  <h3 className="mb-4">Hands-On Learning Experience</h3>
                  <p className="mb-4">
                    Our approach combines theoretical knowledge with practical application. You'll work with 
                    real gemstones, tools, and materials under the guidance of experienced professionals.
                  </p>
                  
                  <div className="luxury-course-phase__topics">
                    <div className="luxury-course-phase__topic">
                      <div className="luxury-course-phase__topic-icon">
                        <FaCheckCircle />
                      </div>
                      <p className="luxury-course-phase__topic-text">Industry-standard tools and equipment</p>
                    </div>
                    <div className="luxury-course-phase__topic">
                      <div className="luxury-course-phase__topic-icon">
                        <FaCheckCircle />
                      </div>
                      <p className="luxury-course-phase__topic-text">Real gemstone specimens for study</p>
                    </div>
                    <div className="luxury-course-phase__topic">
                      <div className="luxury-course-phase__topic-icon">
                        <FaCheckCircle />
                      </div>
                      <p className="luxury-course-phase__topic-text">Workshop simulations and practical exercises</p>
                    </div>
                    <div className="luxury-course-phase__topic">
                      <div className="luxury-course-phase__topic-icon">
                        <FaCheckCircle />
                      </div>
                      <p className="luxury-course-phase__topic-text">Case studies from actual Roha projects</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* Final CTA Section */}
        <section className="luxury-section luxury-section--dark">
          <Container>
            <div className="luxury-section__heading">
              <h2 className="luxury-section__title">Roha is Preparing the Final Course Structure and Launch Details</h2>
              <p className="luxury-section__subtitle">
                The full release will include class format, phase timing, workshop requirements, and registration guidance. 
                For now, this page introduces the curriculum direction and the learning journey we are building.
              </p>
            </div>
            
            <div className="text-center">
              <Link to="/contact" className="wc-button wc-button-primary wc-button-lg me-3">
                Contact Roha for Details
              </Link>
              <Link to="/blogs" className="wc-button wc-button-secondary">
                Read More from Roha
              </Link>
            </div>
            
            <div className="text-center mt-5">
              <div className="luxury-page__hero-tags">
                <span className="luxury-page__hero-tag">Professional Certification</span>
                <span className="luxury-page__hero-tag">Industry Recognition</span>
                <span className="luxury-page__hero-tag">Career Development</span>
                <span className="luxury-page__hero-tag">Practical Skills</span>
              </div>
            </div>
          </Container>
        </section>
      </main>
    </>
  );
};

export default RohaCourse;
